// Updated Inbox.js (full component with ZIP download support)

import React, { useEffect, useState, useRef, useMemo } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  Timestamp,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  deleteDoc,
  getDocs,
} from "firebase/firestore";
import { useTrial } from "../contexts/TrialContext";
import UpgradePrompt from "../components/UpgradePrompt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReceiptUploader from "../components/ReceiptUploader";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CategoryBreakdownChart from "../components/CategoryBreakdownChart";
import { keyframes } from "@mui/system";
import ReceiptSummaryCards from "../components/receipts/ReceiptSummaryCards";
import ReceiptsTableToolbar from "../components/receipts/ReceiptsTableToolbar";
import AddManualReceipt from "../components/receipts/AddManualReceipt";
import DownloadPurchasesDialog from "../components/receipts/DownloadPurchasesDialog";
import {
  Box,
  Button,
  Typography,
  Chip,
  Grid,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Tooltip,
  Stack,
  Checkbox,
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import api from "../services/api";
import CATEGORY_COLORS from "../constants/categoryColors";

function useSortableData(items, config = null) {
  const [sortConfig, setSortConfig] = useState(config);

  const sortedItems = useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        // Custom sort for dates
        if (
          sortConfig.key === "receiptDate" ||
          sortConfig.key === "uploadedAt"
        ) {
          aValue = new Date(aValue || a.uploadedAt?.toDate?.() || 0);
          bValue = new Date(bValue || b.uploadedAt?.toDate?.() || 0);
        }
        // Custom sort for amount/vat (numbers)
        if (sortConfig.key === "amount" || sortConfig.key === "vat") {
          aValue = Number(aValue);
          bValue = Number(bValue);
        }
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
}

const fadeInHighlight = keyframes`
  0% { background-color: #ecfdf5; }
  100% { background-color: transparent; }
`;

// Import centralized expense categories
import { CATEGORY_SELECT_OPTIONS as CATEGORIES } from "../constants/expenseCategories";

function getContrastText(bg) {
  // Simple YIQ formula for contrast; returns "#fff" for dark backgrounds
  if (!bg) return "#333";
  bg = bg.replace("#", "");
  const r = parseInt(bg.substr(0, 2), 16);
  const g = parseInt(bg.substr(2, 2), 16);
  const b = parseInt(bg.substr(4, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 180 ? "#222" : "#fff";
}

function highlightMatch(text, term) {
  if (!term) return text;
  const parts = text.split(new RegExp(`(${term})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <mark key={i} style={{ backgroundColor: "#fff9c4", padding: 0 }}>
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

function Inbox() {
  const uploadSectionRef = useRef(null);

  // Scroll handler
  const scrollToUpload = () => {
    uploadSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { plan, isTrialExpired, trialDaysLeft, isPro } = useTrial();

  const [receipts, setReceipts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewMode, setViewMode] = useState("receipts");
  const [previewFile, setPreviewFile] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [confirmBulkDialogOpen, setConfirmBulkDialogOpen] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    fileName: "",
    amount: "",
    vat: "",
    date: "",
    notes: "",
    category: "",
  });
  const [unmatched, setUnmatched] = useState([]);
  const [editForm, setEditForm] = useState({
    fileName: "",
    amount: "",
    vat: "",
    date: "",
    notes: "",
    category: "",
    projectId: "",
  });

  // const [statusFilter, setStatusFilter] = useState("All");
  const [newlyAddedId, setNewlyAddedId] = useState(null);
  const now = new Date();
  const thisMonth = now.getMonth();
  const [period, setPeriod] = useState("month"); // 'month' | 'year' | 'all'
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [manualExpenseOpen, setManualExpenseOpen] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const saved = sessionStorage.getItem("month");
    const parsed = saved !== null ? Number(saved) : null;
    return parsed !== null && parsed === thisMonth ? parsed : thisMonth;
  });

  const thisYear = now.getFullYear();
  const [selectedYear, setSelectedYear] = useState(() => {
    const saved = sessionStorage.getItem("year");
    const parsed = saved !== null ? Number(saved) : null;
    return parsed === thisYear ? parsed : thisYear;
  });

  const [searchTerm, setSearchTerm] = useState(
    sessionStorage.getItem("searchTerm") || ""
  );
  const [downloadingId, setDownloadingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openManualDialog, setOpenManualDialog] = useState(false);
  const [manualForm, setManualForm] = useState({
    fileName: "",
    amount: "",
    vat: "",
    date: new Date().toISOString().slice(0, 10), // YYYY-MM-DD
    notes: "",
    category: "",
  });

  const filtered = receipts
    .filter((r) => {
      // Filter by period picker
      if (period === "all") return true;
      if (period === "year") return r.receiptYear === year;
      // period === "month"
      return r.receiptYear === year && r.receiptMonth === month;
    })
    .filter((r) =>
      categoryFilter === "All" ? true : r.category === categoryFilter
    )

    .filter((r) => {
      if (!searchTerm) return true;
      const target =
        (r.fileName || "") + " " + (r.notes || "") + " " + (r.category || "");
      return target.toLowerCase().includes(searchTerm.toLowerCase());
    });

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) return;
      const q = query(
        collection(db, "receipts"),
        where("uid", "==", user.uid),
        orderBy("uploadedAt", "desc")
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const all = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const newId = sessionStorage.getItem("newReceiptId");
        if (newId) {
          setNewlyAddedId(newId);
          sessionStorage.removeItem("newReceiptId");
          setTimeout(() => setNewlyAddedId(null), 2000); // Fade highlight after 2s
        }
        setReceipts(all);
      });

      // Fetch projects
      getDocs(query(collection(db, "projects"), where("uid", "==", user.uid)))
        .then((snap) => {
          setProjects(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        })
        .catch((err) => console.error("Failed to fetch projects:", err));

      return () => unsubscribe();
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    const matchedReceipts = receipts.filter((r) => r.matchedTransaction);
  }, [receipts]);

  useEffect(() => {
    const loadUnmatched = async () => {
      try {
        const res = await api.get("/bank/unmatched");
        setUnmatched(res.data.unmatchedTransactions);
      } catch (err) {
        console.error("Failed to load unmatched transactions:", err);
      }
    };
    loadUnmatched();
  }, []);

  useEffect(() => {
    const today = new Date();
    if (today.getDate() === 1) {
      sessionStorage.removeItem("month");
      sessionStorage.removeItem("year");
      sessionStorage.removeItem("searchTerm");
    }
  }, []);

  const user = auth.currentUser;

  const monthlyTotal = filtered
    .filter((r) => !r.pendingReview)
    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  const yearlyTotal = receipts
    .filter((r) => r.receiptYear === selectedYear && !r.pendingReview)
    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  const pendingCount = receipts.filter((r) => r.pendingReview).length;

  const handleDownload = async (receipt) => {
    try {
      setDownloadingId(receipt.id);

      const res = await api.get(`/receipts/${receipt.id}/download`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = receipt.fileName || "receipt";
      a.click();
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "✅ Download complete.",
        severity: "success",
      });
    } catch (err) {
      console.error("Download failed:", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to download file.",
        severity: "error",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleBulkConfirm = async () => {
    const pendingReceipts = receipts.filter(
      (r) =>
        r.pendingReview &&
        r.receiptMonth === selectedMonth &&
        r.receiptYear === selectedYear
    );

    try {
      // You can choose to batch Firestore updates or hit your API
      const updates = pendingReceipts.map((receipt) =>
        api.put(`/receipts/${receipt.id}`, {
          pendingReview: false,
          confirmedAt: new Date().toISOString(), // optional audit field
        })
      );

      await Promise.all(updates);

      setSnackbar({
        open: true,
        message: `✅ Confirmed ${pendingReceipts.length} receipts.`,
        severity: "success",
      });
    } catch (err) {
      console.error("Bulk confirm failed:", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to confirm some receipts.",
        severity: "error",
      });
    }
  };

  const handleDownloadZip = async () => {
    try {
      const res = await api.post(
        "/receipts/zip",
        { receiptIds: selectedIds },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "receipts.zip";
      a.click();
      window.URL.revokeObjectURL(url);
      setSnackbar({
        open: true,
        message: "📦 ZIP download complete.",
        severity: "success",
      });
    } catch (err) {
      console.error("ZIP Download error:", err);
      setSnackbar({
        open: true,
        message: "❌ ZIP download failed.",
        severity: "error",
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedIds.length} receipt(s)? This cannot be undone.`
      )
    ) {
      return;
    }

    try {
      for (const id of selectedIds) {
        await deleteDoc(doc(db, "receipts", id));
      }

      setSnackbar({
        open: true,
        message: `🗑️ Deleted ${selectedIds.length} receipt(s).`,
        severity: "success",
      });

      setSelectedIds([]);
    } catch (err) {
      console.error("Bulk delete error:", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to delete some receipts.",
        severity: "error",
      });
    }
  };

  const filteredUnmatched = unmatched.filter((tx) => {
    const txDate = new Date(tx.date);
    return (
      txDate.getMonth() === selectedMonth &&
      txDate.getFullYear() === selectedYear
    );
  });

  const sortedFiltered = [...filtered].sort(
    (a, b) =>
      (b.uploadedAt?.toDate ? b.uploadedAt.toDate() : new Date(b.uploadedAt)) -
      (a.uploadedAt?.toDate ? a.uploadedAt.toDate() : new Date(a.uploadedAt))
  );

  const {
    items: sortedReceipts,
    requestSort,
    sortConfig,
  } = useSortableData(filtered, { key: "receiptDate", direction: "desc" });

  return (
    <Box sx={{ pb: 4 }}>
      <DownloadPurchasesDialog 
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        receipts={receipts}
      />
      {/* Page Header */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        mb={4}
        gap={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} letterSpacing="-0.02em">
            Expenses
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and manage all your business expenses
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Tooltip
            title={
              !isPro && isTrialExpired
                ? "Your trial has ended. Upgrade to upload receipts."
                : ""
            }
          >
            <span>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={scrollToUpload}
                disabled={!isPro && isTrialExpired}
                sx={{
                  opacity: !isPro && isTrialExpired ? 0.6 : 1,
                  pointerEvents: !isPro && isTrialExpired ? "none" : "auto",
                }}
              >
                Upload Receipt
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export all purchases into a ZIP with a spreadsheet summary">
            <span>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => setDownloadDialogOpen(true)}
                sx={{
                  bgcolor: "background.paper",
                }}
              >
                Export
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              !isPro && isTrialExpired
                ? "Your trial has ended. Upgrade to add expenses."
                : "Add an expense even if you don't have a receipt (e.g. mileage, bank fee, etc)"
            }
          >
            <span>
              <Button
                variant="outlined"
                startIcon={<ReceiptLongIcon />}
                onClick={() => setManualExpenseOpen(true)}
                sx={{
                  bgcolor: "background.paper",
                  opacity: !isPro && isTrialExpired ? 0.6 : 1,
                  pointerEvents: !isPro && isTrialExpired ? "none" : "auto",
                }}
                disabled={!isPro && isTrialExpired}
              >
                Manual Expense
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <ReceiptSummaryCards
        monthlyTotal={monthlyTotal}
        yearlyTotal={yearlyTotal}
        categoryData={Object.entries(
          filtered
            .filter((r) => !r.pendingReview)
            .reduce((acc, r) => {
              const key = r.category || "Uncategorized";
              const amount = parseFloat(r.amount) || 0;
              acc[key] = (acc[key] || 0) + amount;
              return acc;
            }, {})
        ).map(([category, value]) => ({ category, value }))}
      />

      <ReceiptsTableToolbar
        period={period}
        setPeriod={setPeriod}
        month={month}
        setMonth={setMonth}
        year={year}
        setYear={setYear}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      {/* Sort Controls for Bank Statement Reconciliation */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Sort by Receipt Date:
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant={
              sortConfig?.key === "receiptDate" &&
              sortConfig?.direction === "desc"
                ? "contained"
                : "outlined"
            }
            onClick={() => requestSort("receiptDate")}
            sx={{ fontSize: 12 }}
          >
            Newest First ↓
          </Button>
          <Button
            size="small"
            variant={
              sortConfig?.key === "receiptDate" &&
              sortConfig?.direction === "asc"
                ? "contained"
                : "outlined"
            }
            onClick={() => {
              if (
                sortConfig?.key === "receiptDate" &&
                sortConfig?.direction === "desc"
              ) {
                requestSort("receiptDate"); // This will toggle to asc
              } else if (sortConfig?.key !== "receiptDate") {
                requestSort("receiptDate");
                requestSort("receiptDate"); // Toggle twice to get asc
              }
            }}
            sx={{ fontSize: 12 }}
          >
            Oldest First ↑
          </Button>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: "auto" }}
        >
          {sortedReceipts.length} receipts • Matches bank statement order
        </Typography>
      </Box>

      {selectedIds.length > 0 && (
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Button variant="outlined" onClick={handleDownloadZip}>
            📥 Download Selected as ZIP ({selectedIds.length})
          </Button>
          <Button variant="outlined" color="error" onClick={handleBulkDelete}>
            🗑️ Delete Selected ({selectedIds.length})
          </Button>
        </Stack>
      )}

      {plan !== "pro" ||
        (plan !== "proplus" && isTrialExpired && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#fef3c7",
              border: "1px solid #fde68a",
            }}
          >
            <Typography variant="body2">
              Your trial has ended. You can still view existing receipts, but
              creating or sending new ones requires Pro.
            </Typography>
          </Paper>
        ))}
      {filtered.length === 0 && plan !== "pro" && isTrialExpired ? (
        <UpgradePrompt
          source="receipt-page"
          title="🚫 Receipt creation/upload is now disabled."
          subtitle="Your 14-day trial has ended. Upgrade to Pro to continue creating and sending receipts."
        />
      ) : filtered.length === 0 ? null : (
        <Paper sx={{ p: 3 }}>
          {isMobile ? (
            <Stack spacing={2}>
              {filtered.map((receipt) => (
                <Paper key={receipt.id} sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      variant="subtitle2"
                      sx={{ textDecoration: "underline", cursor: "pointer" }}
                      onClick={() =>
                        receipt.imageUrl
                          ? setPreviewFile(receipt)
                          : setSnackbar({
                              open: true,
                              message: "⚠️ No preview available for this file.",
                              severity: "warning",
                            })
                      }
                    >
                      {highlightMatch(receipt.fileName, searchTerm)}
                    </Typography>
                    <IconButton
                      onClick={() => setDeleteTarget(receipt)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>

                  <Typography variant="body2">
                    Total: €{receipt.amount?.toFixed(2) || "—"}
                  </Typography>
                  <Typography variant="body2">
                    VAT: €{receipt.vat?.toFixed(2) || "—"}
                  </Typography>
                  <Typography variant="body2">
                    Category:{" "}
                    {receipt.category ? (
                      <Chip
                        label={
                          CATEGORIES.find((c) => c.value === receipt.category)
                            ?.label ||
                          receipt.category ||
                          "Uncategorized"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            CATEGORY_COLORS[receipt.category] || "#eceff1",
                          color: getContrastText(
                            CATEGORY_COLORS[receipt.category] || "#eceff1"
                          ),
                          fontWeight: 500,
                          fontSize: "0.95rem",
                          px: 2,
                          borderRadius: 2,
                        }}
                      />
                    ) : (
                      <em>Uncategorized</em>
                    )}
                  </Typography>

                  {receipt.notes && (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      📝 {receipt.notes}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} mt={2}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleDownload(receipt)}
                      disabled={downloadingId === receipt.id}
                      startIcon={
                        downloadingId === receipt.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                    >
                      Download
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setEditTarget(receipt);
                        setEditForm({
                          fileName: receipt.fileName || "",
                          amount: receipt.amount || "",
                          vat: receipt.vat || "",
                          date: receipt.receiptDate
                            ? receipt.receiptDate.slice(0, 10)
                            : receipt.uploadedAt?.toDate
                            ? receipt.uploadedAt
                                .toDate()
                                .toISOString()
                                .slice(0, 10)
                            : new Date().toISOString().slice(0, 10),

                          notes: receipt.notes || "",
                          category: receipt.category || "",
                          projectId: receipt.projectId || "",
                        });
                      }}
                    >
                      Edit
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <>
              {receipts.filter((r) => r.pendingReview).length > 0 && (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    ⚠️ You have {receipts.filter((r) => r.pendingReview).length}{" "}
                    auto-created receipt(s) pending review.
                  </Alert>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setConfirmBulkDialogOpen(true)}
                  >
                    Confirm All Pending Receipts ({pendingCount})
                  </Button>
                </>
              )}

              {viewMode === "receipts" ? (
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.length === filtered.length}
                          indeterminate={
                            selectedIds.length > 0 &&
                            selectedIds.length < filtered.length
                          }
                          onChange={(e) =>
                            setSelectedIds(
                              e.target.checked ? filtered.map((r) => r.id) : []
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>File Name</TableCell>
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => requestSort("receiptDate")}
                      >
                        Receipt Date{" "}
                        {sortConfig?.key === "receiptDate"
                          ? sortConfig.direction === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </TableCell>
                      {plan === "proPlus" && (
                        <TableCell>Bank Account</TableCell>
                      )}
                      {plan === "proPlus" && (
                        <TableCell>Bank Transaction Date</TableCell>
                      )}
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => requestSort("uploadedAt")}
                      >
                        Date Uploaded{" "}
                        {sortConfig?.key === "uploadedAt"
                          ? sortConfig.direction === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => requestSort("amount")}
                      >
                        Total (€){" "}
                        {sortConfig?.key === "amount"
                          ? sortConfig.direction === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => requestSort("vat")}
                      >
                        VAT (€){" "}
                        {sortConfig?.key === "vat"
                          ? sortConfig.direction === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedReceipts.map((receipt) => (
                      <TableRow
                        key={receipt.id}
                        hover
                        ref={
                          receipt.id === newlyAddedId
                            ? (el) =>
                                el?.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                })
                            : null
                        }
                        sx={{
                          ...(receipt.id === newlyAddedId && {
                            animation: `${fadeInHighlight} 2s ease`,
                            backgroundColor: "#ecfdf5 !important",
                          }),
                          ...(receipt.pendingReview && {
                            backgroundColor: "#fff7e6",
                          }),
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(receipt.id)}
                            onChange={(e) =>
                              setSelectedIds((prev) =>
                                e.target.checked
                                  ? [...prev, receipt.id]
                                  : prev.filter((id) => id !== receipt.id)
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {receipt.category ? (
                            <Chip
                              label={receipt.category}
                              size="small"
                              sx={{
                                backgroundColor:
                                  CATEGORY_COLORS[receipt.category] ||
                                  "#eceff1",
                                color: getContrastText(
                                  CATEGORY_COLORS[receipt.category] || "#eceff1"
                                ),
                                fontWeight: 500,
                                fontSize: "0.75rem",
                              }}
                            />
                          ) : (
                            <Chip
                              label="Uncategorized"
                              size="small"
                              sx={{
                                backgroundColor: "#f5f5f5",
                                color: "#9e9e9e",
                                fontStyle: "italic",
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction={isMobile ? "column" : "row"}
                            spacing={2}
                          >
                            <InsertDriveFileOutlinedIcon fontSize="small" />
                            <Stack
                              direction={isMobile ? "column" : "row"}
                              spacing={2}
                            >
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                                onClick={() => {
                                  if (receipt.imageUrl) setPreviewFile(receipt);
                                  else
                                    setSnackbar({
                                      open: true,
                                      message:
                                        "⚠️ No preview available for this file.",
                                      severity: "warning",
                                    });
                                }}
                              >
                                <Tooltip
                                  title={
                                    receipt.autoCreated &&
                                    receipt.transactionDescription
                                      ? receipt.transactionDescription
                                      : receipt.fileName
                                  }
                                >
                                  <span>
                                    {highlightMatch(
                                      receipt.autoCreated &&
                                        receipt.transactionDescription
                                        ? receipt.transactionDescription
                                        : receipt.fileName,
                                      searchTerm
                                    )}
                                  </span>
                                </Tooltip>
                              </Typography>
                              {receipt.isManual && (
                                <Tooltip title="Manually entered">
                                  <Chip
                                    label="Manual Entry"
                                    size="small"
                                    color="warning"
                                    sx={{ ml: 1 }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                            {(receipt.amount == null ||
                              receipt.vat == null) && (
                              <Tooltip title="Missing total or VAT">
                                <WarningAmberIcon
                                  fontSize="small"
                                  color="warning"
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {receipt.receiptDate
                            ? new Date(receipt.receiptDate).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                }
                              )
                            : receipt.uploadedAt?.toDate
                            ? new Date(
                                receipt.uploadedAt.toDate()
                              ).toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : "—"}
                        </TableCell>
                        {plan === "proPlus" && (
                          <TableCell>
                            {receipt.matchedTransaction ? (
                              <Tooltip title="This receipt has been linked to a bank transaction for accurate reconciliation.">
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{ width: "100%" }}
                                >
                                  <Chip
                                    label={
                                      <Box textAlign="left" lineHeight={1}>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            color: "white",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          Matched
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: "white", opacity: 0.85 }}
                                        >
                                          Linked to bank
                                        </Typography>
                                      </Box>
                                    }
                                    icon={
                                      <CheckCircleIcon
                                        sx={{ color: "white" }}
                                      />
                                    }
                                    sx={{
                                      backgroundColor: "#16a34a",
                                      borderRadius: "16px",
                                      px: 1.5,
                                      py: 0.5,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            ) : receipt.pendingReview ? (
                              <Tooltip title="This receipt was auto-created and is waiting for your review.">
                                <Chip
                                  label="Pending Review"
                                  size="small"
                                  color="warning"
                                  sx={{
                                    backgroundColor: "#facc15",
                                    color: "#333",
                                    fontWeight: 500,
                                  }}
                                />
                              </Tooltip>
                            ) : receipt.autoCreated &&
                              !receipt.pendingReview ? (
                              <Tooltip title="This receipt was auto-created from a bank transaction and confirmed by you.">
                                <Chip
                                  label="Confirmed Auto-Receipt"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#e0f2fe",
                                    color: "#0369a1",
                                    fontWeight: 500,
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Chip
                                  label="❌ Unmatched"
                                  size="small"
                                  color="default"
                                />
                              </Box>
                            )}
                          </TableCell>
                        )}
                        {plan === "proPlus" && (
                          <TableCell>
                            {receipt.bankTransactionDate
                              ? new Date(
                                  receipt.bankTransactionDate
                                ).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                })
                              : "—"}
                          </TableCell>
                        )}
                        <TableCell>
                          {receipt.uploadedAt?.toDate
                            ? new Date(
                                receipt.uploadedAt.toDate()
                              ).toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              })
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {receipt.amount != null
                            ? `€${receipt.amount.toFixed(2)}`
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {receipt.vat != null
                            ? `€${receipt.vat.toFixed(2)}`
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {receipt.notes ? (
                            <Tooltip title={receipt.notes}>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ maxWidth: 150, color: "text.secondary" }}
                              >
                                {receipt.notes}
                              </Typography>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <IconButton
                              onClick={() => handleDownload(receipt)}
                              size="small"
                              color="primary"
                            >
                              {downloadingId === receipt.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DownloadIcon />
                              )}
                            </IconButton>
                            <IconButton
                              onClick={() => setDeleteTarget(receipt)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setEditTarget(receipt);
                                setEditForm({
                                  fileName: receipt.fileName || "",
                                  amount: receipt.amount || "",
                                  vat: receipt.vat || "",
                                  date: receipt.receiptDate
                                    ? receipt.receiptDate.slice(0, 10)
                                    : receipt.uploadedAt?.toDate
                                    ? receipt.uploadedAt
                                        .toDate()
                                        .toISOString()
                                        .slice(0, 10)
                                    : new Date().toISOString().slice(0, 10),
                                  notes: receipt.notes || "",
                                  category: receipt.category || "",
                                  projectId: receipt.projectId || "",
                                });
                              }}
                              size="small"
                              color="secondary"
                            >
                              ✏️
                            </IconButton>
                          </Stack>
                        </TableCell>
                        {receipt.pendingReview && (
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setReviewTarget(receipt);
                                setReviewForm({
                                  fileName: receipt.fileName || "",
                                  amount: receipt.amount || "",
                                  vat: receipt.vat || "",
                                  date: receipt.receiptDate
                                    ? receipt.receiptDate.slice(0, 10)
                                    : receipt.uploadedAt?.toDate
                                    ? receipt.uploadedAt
                                        .toDate()
                                        .toISOString()
                                        .slice(0, 10)
                                    : new Date().toISOString().slice(0, 10),
                                  notes: receipt.notes || "",
                                  category: receipt.category || "",
                                });
                              }}
                            >
                              Review
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount (€)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUnmatched.length > 0 ? (
                      filteredUnmatched.map((tx) => (
                        <TableRow key={tx.transactionId}>
                          <TableCell>{tx.description || "—"}</TableCell>
                          <TableCell>
                            {new Date(tx.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            €{parseFloat(tx.amount).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            No unmatched transactions for this month.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </Paper>
      )}

      {(plan === "pro" || plan === "proPlus" || !isTrialExpired) && (
        <Paper
          sx={{ p: 3, mb: 4, backgroundColor: "#f9fafb", borderRadius: 3 }}
          ref={uploadSectionRef}
        >
          <ReceiptUploader />
        </Paper>
      )}

      <Dialog
        open={confirmBulkDialogOpen}
        onClose={() => setConfirmBulkDialogOpen(false)}
      >
        <DialogTitle>Confirm All Pending Receipts</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to confirm all {pendingCount} pending receipts
            for this month?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              await handleBulkConfirm(); // Your existing logic for confirming
              setConfirmBulkDialogOpen(false);
              setSnackbar({
                open: true,
                message: `✅ Confirmed ${pendingCount} receipts.`,
                severity: "success",
              });
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            px: 4,
            py: 3,
            minWidth: 320,
            textAlign: "center",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 24, mb: 1 }}>
          Delete Receipt
        </DialogTitle>
        {/* Optionally, add a warning or detail message here */}
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ display: "flex", flexDirection: "column", gap: 1 }}
        >
          <Button
            onClick={() => setDeleteTarget(null)}
            variant="text"
            sx={{
              color: "#153595", // Your brand blue, or theme.palette.primary.main
              fontWeight: 600,
              width: "100%",
              mb: 0.5,
              fontSize: 17,
              letterSpacing: 0,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await deleteDoc(doc(db, "receipts", deleteTarget.id));
              setSnackbar({
                open: true,
                message: "🗑️ Receipt deleted.",
                severity: "success",
              });
              setDeleteTarget(null);
            }}
            color="error"
            variant="contained"
            sx={{
              width: "100%",
              fontWeight: 600,
              fontSize: 17,
              py: 1.2,
              borderRadius: 2,
              background: "#DE3932",
              "&:hover": { background: "#c62828" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Preview: {previewFile?.fileName || "No file selected"}
        </DialogTitle>
        <DialogContent>
          {previewFile?.imageUrl ? (
            previewFile.fileName?.toLowerCase().endsWith(".pdf") ? (
              <iframe
                src={previewFile.imageUrl}
                style={{ width: "100%", height: "80vh", border: "none" }}
                title="PDF Preview"
              />
            ) : (
              <img
                src={previewFile.imageUrl}
                alt={previewFile.fileName}
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  display: "block",
                  margin: "0 auto",
                }}
              />
            )
          ) : (
            <Typography color="text.secondary">
              ❌ No preview available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!reviewTarget}
        onClose={() => setReviewTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon color="warning" />
            <Typography variant="h6" fontWeight={600}>
              Review Bank Transaction
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            This expense was auto-created from your bank feed. Please review and
            categorize it.
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2 }}
        >
          {/* Category at the top - most important for review! */}
          <FormControl fullWidth required>
            <InputLabel>Category *</InputLabel>
            <Select
              value={reviewForm.category}
              onChange={(e) =>
                setReviewForm({ ...reviewForm, category: e.target.value })
              }
              label="Category *"
            >
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Amount and VAT side by side */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Total (€) *"
              type="number"
              value={reviewForm.amount}
              onChange={(e) =>
                setReviewForm({ ...reviewForm, amount: e.target.value })
              }
              fullWidth
              required
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
            />
            <TextField
              label="VAT (€)"
              type="number"
              value={reviewForm.vat}
              onChange={(e) =>
                setReviewForm({ ...reviewForm, vat: e.target.value })
              }
              fullWidth
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
            />
          </Stack>

          <TextField
            label="Date *"
            type="date"
            value={reviewForm.date}
            onChange={(e) =>
              setReviewForm({ ...reviewForm, date: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />

          <TextField
            label="Description / Vendor"
            value={reviewForm.fileName}
            onChange={(e) =>
              setReviewForm({ ...reviewForm, fileName: e.target.value })
            }
            fullWidth
            placeholder="e.g. Amazon, Spotify, etc."
          />

          <TextField
            label="Notes (optional)"
            value={reviewForm.notes}
            onChange={(e) =>
              setReviewForm({ ...reviewForm, notes: e.target.value })
            }
            multiline
            rows={2}
            fullWidth
            placeholder="Any additional details..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await api.put(`/receipts/${reviewTarget.id}`, {
                  fileName: reviewForm.fileName,
                  amount: parseFloat(reviewForm.amount),
                  vat: reviewForm.vat ? parseFloat(reviewForm.vat) : null,
                  notes: reviewForm.notes,
                  category: reviewForm.category,
                  date: reviewForm.date,
                  pendingReview: false, // ✅ Clear review flag
                });
                setSnackbar({
                  open: true,
                  message: "✅ Review confirmed and saved.",
                  severity: "success",
                });
                setReviewTarget(null);
              } catch (err) {
                console.error("Review save failed:", err);
                setSnackbar({
                  open: true,
                  message: "❌ Failed to confirm review.",
                  severity: "error",
                });
              }
            }}
          >
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ReceiptLongIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Edit Expense
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2 }}
        >
          {/* Category at the top - most important! */}
          <FormControl fullWidth required>
            <InputLabel>Category *</InputLabel>
            <Select
              value={editForm.category}
              onChange={(e) =>
                setEditForm({ ...editForm, category: e.target.value })
              }
              label="Category *"
            >
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Project (Optional)</InputLabel>
            <Select
              value={editForm.projectId}
              onChange={(e) =>
                setEditForm({ ...editForm, projectId: e.target.value })
              }
              label="Project (Optional)"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {projects.map((proj) => (
                <MenuItem key={proj.id} value={proj.id}>
                  {proj.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Amount and VAT side by side */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Total (€) *"
              type="number"
              value={editForm.amount}
              onChange={(e) =>
                setEditForm({ ...editForm, amount: e.target.value })
              }
              fullWidth
              required
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
            />
            <TextField
              label="VAT (€)"
              type="number"
              value={editForm.vat}
              onChange={(e) =>
                setEditForm({ ...editForm, vat: e.target.value })
              }
              fullWidth
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
            />
          </Stack>

          <TextField
            label="Date *"
            type="date"
            value={editForm.date}
            onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />

          <TextField
            label="Description / Vendor"
            value={editForm.fileName}
            onChange={(e) =>
              setEditForm({ ...editForm, fileName: e.target.value })
            }
            fullWidth
            placeholder="e.g. Amazon, One4All Voucher, etc."
          />

          <TextField
            label="Notes (optional)"
            value={editForm.notes}
            onChange={(e) =>
              setEditForm({ ...editForm, notes: e.target.value })
            }
            multiline
            rows={2}
            fullWidth
            placeholder="Any additional details..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                const res = await api.put(`/receipts/${editTarget.id}`, {
                  fileName: editForm.fileName,
                  amount: parseFloat(editForm.amount),
                  vat: editForm.vat ? parseFloat(editForm.vat) : null,
                  notes: editForm.notes,
                  category: editForm.category,
                  date: editForm.date,
                  projectId: editForm.projectId || null,
                });
                await api.post(`/receipts/${editTarget.id}/confirm-review`);

                setSnackbar({
                  open: true,
                  message: "✅ Receipt updated.",
                  severity: "success",
                });
                setEditTarget(null);
              } catch (err) {
                console.error("Edit failed:", err);
                setSnackbar({
                  open: true,
                  message: "❌ Failed to update receipt.",
                  severity: "error",
                });
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openManualDialog}
        onClose={() => setOpenManualDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ReceiptLongIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Add Manual Expense
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Add expenses without a receipt (e.g., mileage, bank fees, vouchers)
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2 }}
        >
          {/* Category first - most important! */}
          <FormControl fullWidth required>
            <InputLabel>Category *</InputLabel>
            <Select
              value={manualForm.category}
              onChange={(e) =>
                setManualForm({ ...manualForm, category: e.target.value })
              }
              label="Category *"
            >
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Amount and VAT side by side */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Total (€) *"
              type="number"
              value={manualForm.amount}
              onChange={(e) =>
                setManualForm({ ...manualForm, amount: e.target.value })
              }
              fullWidth
              required
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
            />
            <TextField
              label="VAT (€)"
              type="number"
              value={manualForm.vat}
              onChange={(e) =>
                setManualForm({ ...manualForm, vat: e.target.value })
              }
              fullWidth
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              helperText="Leave blank if no VAT"
            />
          </Stack>

          <TextField
            label="Date *"
            type="date"
            value={manualForm.date}
            onChange={(e) =>
              setManualForm({ ...manualForm, date: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />

          <TextField
            label="Description / Vendor"
            value={manualForm.fileName}
            onChange={(e) =>
              setManualForm({ ...manualForm, fileName: e.target.value })
            }
            fullWidth
            placeholder="e.g. One4All Voucher, AIB Bank Fees, etc."
          />

          <TextField
            label="Notes (optional)"
            value={manualForm.notes}
            onChange={(e) =>
              setManualForm({ ...manualForm, notes: e.target.value })
            }
            multiline
            rows={2}
            fullWidth
            placeholder="Any additional details..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenManualDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!manualForm.amount || !manualForm.category}
            onClick={async () => {
              const user = auth.currentUser;
              if (!user) return;

              const timestamp = new Date(manualForm.date);
              await addDoc(collection(db, "receipts"), {
                uid: user.uid,
                fileName: manualForm.fileName || "Manual Entry",
                amount: parseFloat(manualForm.amount),
                vat: manualForm.vat ? parseFloat(manualForm.vat) : null,
                uploadedAt: Timestamp.fromDate(timestamp),
                receiptMonth: timestamp.getMonth(),
                receiptYear: timestamp.getFullYear(),
                receiptDate: manualForm.date,
                notes: manualForm.notes,
                category: manualForm.category,
                isManual: true,
              });

              setSnackbar({
                open: true,
                message: "📝 Manual receipt added.",
                severity: "success",
              });

              setManualForm({
                fileName: "",
                amount: "",
                vat: "",
                date: new Date().toISOString().slice(0, 10),
                notes: "",
                category: "",
              });
              setOpenManualDialog(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <AddManualReceipt
        open={manualExpenseOpen}
        onClose={() => setManualExpenseOpen(false)}
        user={user}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Inbox;
